import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { FilterValue, SortState } from '@/components/data/types'
import { isInert } from '@/components/data/filters'
import {
  decodeFilters, decodeQuick, decodeSort, encodeFilters, encodeQuick, encodeSort,
} from './tableUrlCodec'

/**
 * One table's sort, column filters and quick filters, in the URL.
 *
 * ── A SIBLING OF `useUrlState`, NOT AN EXTENSION OF IT ──────────────────────
 * `useUrlState` is a fixed-shape global: a four-key interface, one instance per
 * page, and a `DEFAULTS` record its delete-if-default rule indexes by literal
 * key. Table state is per-INSTANCE with a variable key set and several
 * instances per page — the storage screen has two tables, an organization's
 * detail page has three, the sources screen has two, and the calibration screen
 * has one per price table it discovers at runtime. Folding that in would make
 * `UrlState` unbounded and break `DEFAULTS[k]`.
 *
 * It obeys `useUrlState`'s three rules verbatim: remapped parameter names, a
 * value equal to its default is DELETED rather than written, and every write is
 * `replace: true` so dragging a control does not fill the back stack.
 *
 * ── AND IT DIFFERS IN EXACTLY ONE WAY, WHICH IS FORCED ──────────────────────
 * It writes through the FUNCTIONAL `setSearchParams(prev => …)` form.
 * `useUrlState` builds `new URLSearchParams(params)` from the snapshot it
 * captured at render, which is correct for one writer. With three tables on one
 * page, two writes in the same tick would each start from the same stale
 * snapshot and the second would silently drop the first.
 */
export interface TableState {
  sort: SortState
  filters: Record<string, FilterValue>
  quick: string[]
  setSort: (s: SortState) => void
  setFilter: (columnId: string, f: FilterValue | null) => void
  toggleQuick: (id: string) => void
  clearAll: () => void
  /** True when anything is hiding or reordering rows. */
  isActive: boolean
}

/**
 * Which table ids are currently mounted, in development only.
 *
 * ── WHY A RUNTIME CHECK AND NOT A STATIC REGISTRY ───────────────────────────
 * Two tables sharing an id share one URL namespace, so filtering one silently
 * filters the other — and the storage screen has two tables, an organization's
 * detail page three, the sources screen two. A hand-maintained list of literals
 * would catch those. It would NOT catch the calibration screen, whose table ids
 * are generated per price table discovered at runtime from `pg_trigger`: the
 * number of tables is not known at build time, so a static assertion cannot
 * cover the one case most likely to collide.
 */
const mounted = new Map<string, number>()

/**
 * The last params object we produced, and the router state it was derived from.
 *
 * ── WHY THIS EXISTS: THE FUNCTIONAL FORM DOES NOT BATCH ─────────────────────
 * `setSearchParams(prev => …)` looks like `setState`'s functional form and is
 * not: react-router hands the updater the value from a ref it refreshes during
 * RENDER, so two calls inside one event handler both receive the same value and
 * the second navigation clobbers the first. An organization's detail page
 * renders three of these tables, so losing a write is a filter that silently
 * does not apply — and `useTableState.test.tsx` reproduces it in both shapes:
 * two tables writing one key each, and one table writing two columns.
 *
 * The accumulator closes it without a microtask. A write reuses the previous
 * result only while the router has not yet caught up with it — which is exactly
 * the definition of "still inside the same batch" — and reseeds from the router
 * on the next render, or whenever anything else changed the URL.
 */
let accumulated: { from: string; params: URLSearchParams } | null = null
let scheduledReset = false

/**
 * The accumulator must live exactly as long as ONE event handler, and
 * `accumulated.from === from` cannot express that.
 *
 * `from` is the serialized URL, so it repeats. The third sort state deletes the
 * key and returns the URL to the value it had before the first click — which
 * MATCHES the entry left there by that click, and reuses a params object still
 * carrying the sort the operator just removed. Clicking a header three times
 * therefore put the first sort back, which reads on screen as a table that
 * flickers and will not sort. Reported from `/data-quality`, whose «Alcance»
 * column is the one people cycle.
 *
 * A microtask is the real boundary: everything inside one synchronous handler
 * composes, and anything after it starts from the router. The `from` check stays
 * as a second condition — it is still what makes a write see its predecessor
 * within the batch.
 */
function dropAccumulatorAfterThisBatch(): void {
  if (scheduledReset) return
  scheduledReset = true
  queueMicrotask(() => {
    accumulated = null
    scheduledReset = false
  })
}

function editParams(
  current: URLSearchParams,
  key: string,
  produce: (v: string | null) => string | null,
): URLSearchParams {
  const from = current.toString()
  const base =
    accumulated && accumulated.from === from
      ? accumulated.params
      : new URLSearchParams(current)
  const value = produce(base.get(key))
  // DELETE rather than write an empty value: `useUrlState`'s rule, so the
  // common link stays short and readable in a Slack preview.
  if (value === null) base.delete(key)
  else base.set(key, value)
  accumulated = { from, params: base }
  dropAccumulatorAfterThisBatch()
  return base
}

export function useTableState(tableId: string): TableState {
  const [params, setParams] = useSearchParams()

  useEffect(() => {
    if (!import.meta.env.DEV) return
    const n = (mounted.get(tableId) ?? 0) + 1
    mounted.set(tableId, n)
    if (n > 1) {
      console.error(
        `[DataTable] two mounted tables share the id "${tableId}". They share one ` +
          'URL namespace, so filtering one will silently filter the other.',
      )
    }
    return () => {
      const left = (mounted.get(tableId) ?? 1) - 1
      if (left <= 0) mounted.delete(tableId)
      else mounted.set(tableId, left)
    }
  }, [tableId])

  const keys = useMemo(
    () => ({ s: `${tableId}.s`, f: `${tableId}.f`, q: `${tableId}.q` }),
    [tableId],
  )

  const sort = useMemo(() => decodeSort(params.get(keys.s)), [params, keys.s])
  const filters = useMemo(() => decodeFilters(params.get(keys.f)), [params, keys.f])
  const quick = useMemo(() => decodeQuick(params.get(keys.q)), [params, keys.q])

  const write = useCallback(
    (key: string, produce: (current: string | null) => string | null) => {
      setParams(editParams(params, key, produce), { replace: true })
    },
    [setParams, params],
  )

  const setSort = useCallback(
    (s: SortState) => write(keys.s, () => encodeSort(s)),
    [write, keys.s],
  )

  const setFilter = useCallback(
    (columnId: string, f: FilterValue | null) => {
      write(keys.f, (current) => {
        const next = decodeFilters(current)
        // An inert filter — an empty search box, no boxes ticked — hides
        // nothing, so its key is DELETED rather than written as an empty
        // payload. That is `useUrlState`'s delete-if-default rule, which exists
        // so the common link stays short and readable in a Slack preview.
        if (f === null || isInert(f)) delete next[columnId]
        else next[columnId] = f
        return encodeFilters(next)
      })
    },
    [write, keys.f],
  )

  const toggleQuick = useCallback(
    (id: string) => {
      write(keys.q, (current) => {
        const now = decodeQuick(current)
        return encodeQuick(now.includes(id) ? now.filter((q) => q !== id) : [...now, id])
      })
    },
    [write, keys.q],
  )

  const clearAll = useCallback(() => {
    // ── THROUGH THE ACCUMULATOR, LIKE EVERY OTHER WRITE ──────────────────
    // This used to call `setParams` directly, which left the module-level
    // accumulator holding the params from BEFORE the clear. The next write —
    // ticking one checkbox — reused that stale object and put every cleared
    // filter back. Two writes, and the second silently undid the first.
    //
    // The SORT survives a filter clear on purpose: they are different acts, and
    // losing an ordering you chose because you cleared a search is the kind of
    // small betrayal that makes a control feel unreliable.
    write(keys.f, () => null)
    write(keys.q, () => null)
  }, [write, keys.f, keys.q])

  const isActive =
    Object.keys(filters).length > 0 || quick.length > 0 || sort.kind === 'column'

  return { sort, filters, quick, setSort, setFilter, toggleQuick, clearAll, isActive }
}

/**
 * Drop every table key that does not belong to the page now on screen.
 *
 * Internal tabs mean a table leaves the tree while its `<tableId>.f` stays in
 * the URL, so a link to a section would carry filters from a pane the recipient
 * cannot see — and cannot clear, because the control that set them is not
 * rendered. Called by `SectionLayout` with the tab that is open.
 *
 * The prefix is matched against the OPEN TAB rather than against a registry of
 * ids, because the calibration screen generates one table per price table it
 * discovers at runtime: the set is not knowable at build time.
 */
export function pruneTableKeys(params: URLSearchParams, openTab: string | null): URLSearchParams {
  const next = new URLSearchParams(params)
  const allowed = openTab === null ? [] : (TABLE_OWNERS[openTab] ?? [])
  for (const key of [...next.keys()]) {
    const m = /^(.+)\.(s|f|q)$/.exec(key)
    if (!m) continue
    const owner = m[1]
    // Prefixes, not exact ids: `cal3` belongs to `cal` and `odMembers` to `od`,
    // because one screen can render several tables and the calibration screen
    // does not know how many until it has the data.
    if (!allowed.some((p) => owner.startsWith(p))) next.delete(key)
  }
  return next
}

/**
 * Which table-id prefixes each TAB owns.
 *
 * Keyed by the tab that is open, and a list because one screen can render
 * several tables. `/orgs` owns both `orgs` (the roster) and `od` (the three
 * tables on an organization's detail page): `activeTab('/orgs/acme')` resolves
 * to `/orgs`, so without `od` here, opening a customer would erase the filters
 * on the customer's own tables.
 *
 * Kept beside the hook rather than in the nav table because it is a fact about
 * the tables, not about the navigation.
 */
export const TABLE_OWNERS: Record<string, string[]> = {
  // A tab with tables and NO entry here is not inert — it is broken. `allowed`
  // becomes `[]`, so the effect in `SectionLayout` deletes every table key on
  // the very render that wrote it: the sort lands in the URL and is erased
  // before the table can read it back. On screen that is a header click that
  // flickers and resets, on every column, which is how it was reported from
  // `/data-quality`. `every_table_id_has_an_owner` in the test file is what
  // stops the next table shipping the same way.
  '/': ['dash'],
  // Las tablas gemelas de «Evolución»: `trendsPeople`, `trendsOrgs`,
  // `trendsPaying`, `trendsCpu`, `trendsSandbox`, `trendsActivity`,
  // `trendsOptimizations`, `trendsConcentration`, `trendsTurns`,
  // `trendsAgentPeople`, `trendsCost` y la tabla de `trendsCohorts` — y las de
  // las secciones que vengan, con el mismo prefijo.
  '/trends': ['trends'],
  '/data-quality': ['dq'],
  '/admin': ['adm'],
  '/orgs': ['orgs', 'od'],
  '/people': ['people'],
  '/access': ['accessQueue'],
  '/contacts': ['contactQueue'],
  '/funnel': ['funnel'],
  '/activity': ['act'],
  '/money/margin': ['margin'],
  '/redemptions': ['redeem'],
  // Un prefijo para las cuatro: la lista (`promos`) y las tres tablas de la
  // ficha (`promoAssignees`, `promoRedemptions`, `promoEvents`), porque
  // `activeTab('/promos/<id>')` resuelve a `/promos` — el mismo caso que `od`.
  '/promos': ['promo'],
  '/storage': ['stg'],
  '/projections': ['brk'],
  '/sources': ['src'],
  '/calibration': ['cal'],
  // `incidents`, no `watch`: la pantalla conservó su ruta y su etiqueta —
  // `nav.ts` explica por qué no se le inventa un segundo nombre — pero su
  // tabla ya no muestra hallazgos, muestra incidencias con culpa y
  // consecuencia. Un `tableId` que miente sobre lo que ordena es una
  // sorpresa esperando en la URL de alguien.
  '/watch': ['incidents'],
  // `/communications` owns the detail page's table too: `activeTab` resolves
  // `/communications/<id>` to `/communications` by longest prefix, exactly as
  // `/orgs` owns `od`. Without `commRcpt` here, opening one communication would
  // erase the sort on its own recipients table.
  '/communications': ['commList', 'commRcpt'],
  '/communications/audience': ['aud'],
  '/communications/webinars': ['webi'],
}
